import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrackedTabs, TabsContent, TabsList, TabsTrigger } from '@/components/attention/TrackedTabs';
import { TrackedKaiChat } from '@/components/attention/TrackedKaiChat';
import { AttentionZone } from '@/components/attention/AttentionZone';
import { AttentionProvider, useAttention } from '@/components/attention/AttentionContext';
import { useTranslation } from 'react-i18next';
import { Interview } from '@/types/interview';
import AISummary from './AISummary';
import Innspill from './Innspill';
import ChatHistory from './ChatHistory';
import QuizSection from '@/components/quiz/QuizSection';
import { CommentSection } from '@/components/social/CommentSection';
import { DebateSection } from '@/components/social/DebateSection';
import { PollSection } from '@/components/poll/PollSection';
import { useAiChat } from '@/hooks/interview/useAiChat';
import { ArticleSources } from './ArticleSources';

interface InterviewExpandedProps {
  interview: Interview;
}

const InterviewExpandedContent: React.FC<InterviewExpandedProps> = ({ interview }) => {
  const { t } = useTranslation();
  const { aiChat, handleAiQuestion, handleAiResponse } = useAiChat();
  const attention = useAttention();

  // Track Kai messages with attention tracking
  const handleKaiQuestion = (question: string) => {
    if (attention) {
      attention.trackKaiMessage(question);
    }
    handleAiQuestion(question);
  };

  return (
    <Card className="mt-4 md:border md:shadow-sm border-0 shadow-none">
      <CardContent className="pt-4 md:pt-6 px-0 md:px-6">
        <TrackedTabs defaultValue="ai-summary" className="w-full">
          <TabsList className="grid grid-cols-2 md:flex md:flex-row w-full gap-2 mb-20 md:mb-4 p-0 bg-transparent px-4 md:px-0">
            <TabsTrigger value="ai-summary" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.summary')}</TabsTrigger>
            <TabsTrigger value="innspill" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.input')}</TabsTrigger>
            <TabsTrigger value="debates" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.debates')}</TabsTrigger>
            <TabsTrigger value="comments" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.aiChat')}</TabsTrigger>
            <TabsTrigger value="poll" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.poll')}</TabsTrigger>
            <TabsTrigger value="quiz" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.quiz')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="ai-summary" className="mt-4 px-4 md:px-0">
            <AttentionZone zoneId="summary">
              <AISummary 
                aiSummary={interview.aiSummary || []}
                aiResponse=""
                summaryStatus={interview.summary_status as any}
                summaryError={interview.summary_error}
                context={interview.context}
                question={interview.question}
                answer={interview.answer}
                intervieweeName={interview.intervieweeName}
                title={interview.title}
                company={interview.company}
                interviewId={interview.id}
              />
            </AttentionZone>
          </TabsContent>
          
          <TabsContent value="innspill" className="mt-4 px-4 md:px-0">
            <AttentionZone zoneId="innspill">
              <Innspill interviewId={interview.id} />
            </AttentionZone>
          </TabsContent>
          
          <TabsContent value="debates" className="mt-4 px-4 md:px-0">
            <AttentionZone zoneId="debate">
              <DebateSection interviewId={interview.id} />
            </AttentionZone>
          </TabsContent>
          
          <TabsContent value="comments" className="mt-4 px-4 md:px-0">
            <AttentionZone zoneId="kai">
              <ChatHistory 
                onSubmit={handleKaiQuestion}
                onResponse={handleAiResponse}
                context={`
ARTIKKEL: ${interview.title}

EKSPERT SOM BLIR INTERVJUET: ${interview.intervieweeName} ${interview.company ? `fra ${interview.company}` : ''}

INTERVJUSPØRSMÅL: ${interview.question}
EKSPERTENS SVAR: ${interview.answer}

ARTIKKELINNHOLD: ${interview.context}

AI-OPPSUMMERING: ${Array.isArray(interview.aiSummary) ? interview.aiSummary.join('\n') : interview.aiSummary || ''}

${interview.aiResponse ? `TIDLIGERE SVAR FRA GEMENIKI: ${interview.aiResponse}` : ''}
                `.trim()}
                chat={aiChat}
              />
            </AttentionZone>
          </TabsContent>
          
          <TabsContent value="poll" className="mt-4 px-4 md:px-0">
            <AttentionZone zoneId="poll">
              <PollSection interviewId={interview.id} />
            </AttentionZone>
          </TabsContent>
          
          <TabsContent value="quiz" className="mt-4 px-4 md:px-0">
            <AttentionZone zoneId="quiz">
              <QuizSection interviewId={interview.id} />
            </AttentionZone>
          </TabsContent>
        </TrackedTabs>
      </CardContent>
    </Card>
  );
};

export const InterviewExpanded: React.FC<InterviewExpandedProps> = ({ interview }) => {
  return (
    <AttentionProvider interviewId={interview.id} enabled={true}>
      <InterviewExpandedContent interview={interview} />
    </AttentionProvider>
  );
};
